import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiGet } from '../../services/api'
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  Plus
} from 'lucide-react'

interface Submission {
  id: number
  indicator_id: number
  submission_date: string
  status: string
  notes?: string
}

interface Indicator {
  id: number
  code: string
  name: string
  target: number
  max_points: number
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export default function KPICalendarPage() {
  const navigate = useNavigate()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [indicators, setIndicators] = useState<Indicator[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subs, inds] = await Promise.all([
          apiGet<Submission[]>('/kpi/submissions').catch(() => []),
          apiGet<Indicator[]>('/kpi/indicators').catch(() => [])
        ])
        // Ensure we always have arrays
        setSubmissions(Array.isArray(subs) ? subs : [])
        setIndicators(Array.isArray(inds) ? inds : [])
      } catch (err) {
        console.error('Error fetching data:', err)
        setSubmissions([])
        setIndicators([])
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  const getIndicatorById = (id: number) => indicators.find(i => i.id === id)

  const getSubmissionsForDate = (date: Date): Submission[] => {
    const dateStr = date.toISOString().split('T')[0]
    return submissions.filter(s => s.submission_date === dateStr)
  }

  const generateCalendarDays = () => {
    const days = []
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startDay = firstDay.getDay()
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // Previous month
    for (let i = startDay - 1; i >= 0; i--) {
      const date = new Date(year, month, -i)
      days.push({ date, isCurrentMonth: false, isToday: false })
    }

    // Current month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day)
      date.setHours(0, 0, 0, 0)
      days.push({
        date,
        isCurrentMonth: true,
        isToday: date.getTime() === today.getTime()
      })
    }

    // Next month
    const remaining = 42 - days.length
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false, isToday: false })
    }

    return days
  }

  const monthlySubmissions = submissions.filter(s => {
    const d = new Date(s.submission_date)
    return d.getFullYear() === year && d.getMonth() === month
  })

  const calendarDays = generateCalendarDays()
  const selectedSubmissions = getSubmissionsForDate(selectedDate)

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
      </div>
    )
  }

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">KPI Calendar</h1>
        <div className="flex gap-2">
          <button
            onClick={() => { setCurrentDate(new Date()); setSelectedDate(new Date()) }}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Today
          </button>
          <button
            onClick={() => navigate('/kpi')}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1"
          >
            <Plus className="h-4 w-4" /> Submit KPI
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-2xl font-bold">{monthlySubmissions.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <p className="text-sm text-gray-500">Approved</p>
          <p className="text-2xl font-bold text-green-600">
            {monthlySubmissions.filter(s => s.status === 'approved').length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <p className="text-sm text-gray-500">Pending</p>
          <p className="text-2xl font-bold text-blue-600">
            {monthlySubmissions.filter(s => s.status === 'submitted').length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <p className="text-sm text-gray-500">Rejected</p>
          <p className="text-2xl font-bold text-red-600">
            {monthlySubmissions.filter(s => s.status === 'rejected').length}
          </p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Calendar */}
        <div className="flex-1 bg-white rounded-lg shadow-sm border overflow-hidden">
          {/* Month Navigation */}
          <div className="flex items-center justify-between p-4 bg-gray-50 border-b">
            <button
              onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
              className="p-2 hover:bg-gray-200 rounded-lg"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <h2 className="text-lg font-semibold">{MONTH_NAMES[month]} {year}</h2>
            <button
              onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
              className="p-2 hover:bg-gray-200 rounded-lg"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 bg-gray-50 border-b">
            {DAY_NAMES.map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium text-gray-600">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7">
            {calendarDays.map((day, idx) => {
              const daySubs = getSubmissionsForDate(day.date)
              const isSelected = selectedDate.toDateString() === day.date.toDateString()

              return (
                <div
                  key={idx}
                  onClick={() => setSelectedDate(day.date)}
                  className={`
                    min-h-[80px] p-2 border-b border-r cursor-pointer transition-colors
                    ${!day.isCurrentMonth ? 'bg-gray-50 text-gray-400' : 'hover:bg-blue-50'}
                    ${day.isToday ? 'bg-blue-50' : ''}
                    ${isSelected ? 'ring-2 ring-blue-500 ring-inset' : ''}
                  `}
                >
                  <div className={`
                    text-sm mb-1 w-7 h-7 flex items-center justify-center
                    ${day.isToday ? 'bg-blue-600 text-white rounded-full' : ''}
                  `}>
                    {day.date.getDate()}
                  </div>
                  <div className="space-y-1">
                    {daySubs.slice(0, 2).map(sub => {
                      const ind = getIndicatorById(sub.indicator_id)
                      const color = sub.status === 'approved' ? 'bg-green-500' :
                                    sub.status === 'rejected' ? 'bg-red-500' : 'bg-blue-500'
                      return (
                        <div key={sub.id} className={`text-xs px-1 py-0.5 rounded text-white truncate ${color}`}>
                          {ind?.code || 'KPI'}
                        </div>
                      )
                    })}
                    {daySubs.length > 2 && (
                      <div className="text-xs text-gray-500">+{daySubs.length - 2} more</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Selected Date Panel */}
        <div className="lg:w-80 bg-white rounded-lg shadow-sm border">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold">
              {selectedDate.toLocaleDateString('en-US', {
                weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
              })}
            </h3>
          </div>
          <div className="p-4">
            {selectedSubmissions.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-4">No submissions</p>
                <button
                  onClick={() => navigate('/kpi')}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Submission
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedSubmissions.map(sub => {
                  const ind = getIndicatorById(sub.indicator_id)
                  const StatusIcon = sub.status === 'approved' ? CheckCircle :
                                     sub.status === 'rejected' ? AlertCircle : Clock
                  const statusColor = sub.status === 'approved' ? 'text-green-600' :
                                      sub.status === 'rejected' ? 'text-red-600' : 'text-blue-600'
                  return (
                    <div key={sub.id} className="p-3 border rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{ind?.code} - {ind?.name}</p>
                          {sub.notes && <p className="text-sm text-gray-500 mt-1">{sub.notes}</p>}
                        </div>
                        <StatusIcon className={`h-5 w-5 ${statusColor}`} />
                      </div>
                      <span className={`
                        inline-block mt-2 px-2 py-0.5 text-xs font-medium rounded
                        ${sub.status === 'approved' ? 'bg-green-100 text-green-800' : ''}
                        ${sub.status === 'submitted' ? 'bg-blue-100 text-blue-800' : ''}
                        ${sub.status === 'rejected' ? 'bg-red-100 text-red-800' : ''}
                      `}>
                        {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
